import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { FiAlertCircle } from 'react-icons/fi';
import DNATraitVoting from '../components/DNATraitVoting.js';
import StreamerDNAWidget from '../components/StreamerDNAWidget.js';
import VideoStream from '../components/VideoStream';
import StreamChat from '../components/StreamChat';
import { db } from '../firebase.js';
import { 
  doc, 
  getDoc, 
  onSnapshot, 
  collection, 
  query, 
  where, 
  getDocs, 
  setDoc, 
  serverTimestamp, 
  updateDoc, 
  increment 
} from 'firebase/firestore';

const Stream = () => {
  const { username } = useParams();
  const { currentUser, loading: authLoading } = useAuth();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const chatContainerRef = useRef(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [dnaVoteCooldown, setDnaVoteCooldown] = useState(false);
  const [streamDna, setStreamDna] = useState({
    traits: {
      funny: { score: 0 },
      clutch: { score: 0 },
      unhinged: { score: 0 },
      leader: { score: 0 },
      troll: { score: 0 }
    }
  });
  
  // Fetch stream data
  useEffect(() => {
    if (!username) return;

    const fetchStreamData = async () => {
      try {
        const streamRef = doc(db, 'streams', username);
        const unsubscribe = onSnapshot(streamRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            setStream({
              id: doc.id,
              ...data
            });
            setStreamDna(data.dna || streamDna);
          } else {
            setError('Stream not found');
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('Error fetching stream:', err);
        setError('Failed to load stream');
        setLoading(false);
      }
    };

    fetchStreamData();
  }, [username]);
  
  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);
  
  // Handle sending a chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() || !currentUser || !stream) return;
    
    try {
      const messageRef = doc(collection(db, 'streams', stream.id, 'chat'));
      await setDoc(messageRef, {
        userId: currentUser.uid,
        username: currentUser.displayName || currentUser.email,
        message: messageInput.trim(),
        timestamp: serverTimestamp(),
        isSubscriber: true, // TODO: Check actual subscriber status
        isModerator: false // TODO: Check actual moderator status
      });
      
      setMessageInput('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };
  
  // Format time duration
  const formatDuration = (startTime) => {
    const durationMs = Date.now() - startTime;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };
  
  // Add DNA vote handling
  const handleDnaVote = async (traitKey) => {
    if (!currentUser || !stream) return;
    
    try {
      // Update DNA trait score in Firestore
      const streamRef = doc(db, 'streams', stream.id);
      await updateDoc(streamRef, {
        [`dna.traits.${traitKey}.score`]: increment(1)
      });
      
      // Update local state
      setStreamDna(prev => ({
        traits: {
          ...prev.traits,
          [traitKey]: {
            score: (prev.traits[traitKey]?.score || 0) + 1
          }
        }
      }));
      
      // Set cooldown
      setDnaVoteCooldown(true);
      setTimeout(() => {
        setDnaVoteCooldown(false);
      }, 300000); // 5 minutes
      
      // Add vote message to chat
      const messageRef = doc(collection(db, 'streams', stream.id, 'chat'));
      await setDoc(messageRef, {
        userId: currentUser.uid,
        username: currentUser.displayName || currentUser.email,
        message: `Voted for ${traitKey} DNA trait!`,
        timestamp: serverTimestamp(),
        isSystem: true
      });
    } catch (err) {
      console.error('Error voting for DNA trait:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-800 rounded-lg mb-6"></div>
          <div className="h-48 bg-gray-800 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-500/10 text-red-500 p-6 rounded-lg flex items-start">
          <FiAlertCircle className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium mb-1">Authentication Required</h3>
            <p>Please sign in to access streaming features.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-500/10 text-red-500 p-6 rounded-lg flex items-start">
          <FiAlertCircle className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium mb-1">Error Loading Stream</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="bg-black rounded-lg overflow-hidden aspect-video mb-6">
            <VideoStream streamKey={stream.id} />
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">{stream.title}</h1>
            <p className="text-gray-400">{stream.description}</p>
            
            <div className="mt-6">
              <h2 className="text-xl font-semibold text-white mb-4">Streamer DNA</h2>
              <StreamerDNAWidget dna={streamDna} />
              <DNATraitVoting streamId={stream.id} />
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-1">
          <StreamChat streamId={stream.id} />
        </div>
      </div>
    </div>
  );
};

export default Stream; 